import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useCallback,
  ReactNode,
} from 'react';
import {
  BankAccount,
  CreditCard,
  Investment,
  CryptoHolding,
  PrivateEquity,
  HSA,
  CreditScore,
  Asset,
  Liability,
  Retirement401k,
  BudgetCategory,
  Goal,
  NetWorthSnapshot,
  UndoAction,
  EntityType,
  MarketIndex,
  RetirementSettings,
  UserSettings,
  DashboardView,
  WidgetKey,
  CheckIn,
} from '../types';
import { summarizeStreak, toDateKey } from '../utils/streak';
import {
  DEFAULT_BANK_ACCOUNTS,
  DEFAULT_CREDIT_CARDS,
  DEFAULT_ASSETS,
  DEFAULT_LIABILITIES,
  TIER_LIMITS,
} from '../config/constants';
import {
  loadDataFromSheets as loadFromSheets,
  updateSheetValue,
  addInvestment,
  batchAddInvestments,
  batchUpdateInvestments,
  addBankAccount,
  addCreditCard,
  addAsset,
  addHSA,
  addLiability,
  add401kHolding,
  batchAdd401kHoldings,
  clearAll401kHoldings,
  addCryptoHolding,
  addPrivateEquityHolding,
  addCreditScore,
  addMarketTicker,
  createTemplateSheet,
  createSheetWithData,
  deleteRow,
  deleteRows,
  upsertNetWorthSnapshot,
  appendCheckIn,
  addBudgetCategory as addBudgetCategoryToSheet,
  addGoal as addGoalToSheet,
  saveUserSettings,
} from '../services/googleSheetsService';
import { fetchCryptoPrices, fetchCryptoPeriodReturns, resolveSymbolToId, applyCachedCryptoPrices } from '../services/coinGeckoService';
import { fetchPeriodReturnsForSymbols } from '../services/stockPeriodReturnsService';
import {
  calculateTotalBanking,
  calculateTotalCredit,
  calculateTotalInvestments,
  calculateTotal401k,
  calculateTotalCrypto,
  calculateTotalPrivateEquity,
  calculateTotalHSA,
  calculateTotalAssets,
  calculateTotalLiabilities,
  calculateNetWorth,
  calculateTotalBudgeted,
  calculateTotalSpent,
  calculateTotalGoalsSaved,
  calculateTotalGoalsTarget,
} from '../utils/calculations';
import { PaymentTier } from '../types';

interface FinancialDataContextType {
  // Data
  bankingData: BankAccount[];
  creditCards: CreditCard[];
  investments: Investment[];
  cryptoHoldings: CryptoHolding[];
  retirement401k: Retirement401k[];
  privateEquityHoldings: PrivateEquity[];
  hsaAccounts: HSA[];
  creditScores: CreditScore[];
  marketSnapshot: MarketIndex[];
  retirementSettings: RetirementSettings | null;
  updateRetirementSettings: (settings: RetirementSettings) => void;
  userSettings: UserSettings;
  updateDashboardView: (view: DashboardView, sheetId: string) => Promise<void>;
  toggleWidgetVisibility: (widget: WidgetKey, sheetId: string) => Promise<void>;
  handleAddMarketTicker: (symbol: string, label: string, sheetId: string) => Promise<number>;
  handleDeleteMarketTicker: (index: number, sheetId: string) => Promise<void>;
  assets: Asset[];
  liabilities: Liability[];
  budgetCategories: BudgetCategory[];
  goals: Goal[];
  netWorthHistory: NetWorthSnapshot[];

  // Weekly check-in streak
  checkIns: CheckIn[];
  /** Set when a check-in starts a new week, so the UI can celebrate it once. */
  checkInEvent: CheckInEvent | null;
  dismissCheckInEvent: () => void;
  confirmUpToDate: () => void;

  // Computed totals
  totalBanking: number;
  totalCreditUsed: number;
  totalCreditLimit: number;
  totalInvestmentValue: number;
  totalCrypto: number;
  total401k: number;
  totalPrivateEquity: number;
  totalHSA: number;
  totalAssets: number;
  totalLiabilities: number;
  netWorth: number;
  totalBudgeted: number;
  totalSpent: number;
  totalGoalsSaved: number;
  totalGoalsTarget: number;

  // Undo
  undoStack: UndoAction[];
  handleUndo: () => Promise<void>;

  // Loading
  isLoading: boolean;
  loadDataFromSheets: (sheetId: string) => Promise<{ recreated: boolean; newSheetId?: string }>;
  createNewSheet: () => Promise<string>;

  // Update functions
  updateBankingData: (index: number, balance: number, sheetId: string) => Promise<void>;
  updateCreditCard: (index: number, field: 'balance' | 'limit', value: number, sheetId: string) => Promise<void>;
  updateAsset: (index: number, value: number, sheetId: string) => Promise<void>;
  updateLiability: (index: number, field: 'value' | 'interestRate', value: number, sheetId: string) => Promise<void>;
  updateInvestment: (index: number, field: 'symbol' | 'shares' | 'costBasis', value: number | string, sheetId: string) => Promise<void>;
  updateCryptoHolding: (index: number, field: 'symbol' | 'amount' | 'costBasis', value: number | string, sheetId: string) => Promise<void>;
  update401kHolding: (index: number, field: 'balance' | 'shares' | 'costBasis' | 'fundName' | 'ticker', value: number | string, sheetId: string) => Promise<void>;
  updatePrivateEquityHolding: (index: number, field: 'shares' | 'pricePerShare' | 'costBasis' | 'name' | 'symbol', value: number | string, sheetId: string) => Promise<void>;
  updateHSA: (index: number, balance: number, sheetId: string) => Promise<void>;
  updateCreditScore: (index: number, score: number, sheetId: string) => Promise<void>;
  updateBudgetCategory: (index: number, field: 'budgeted' | 'spent', value: number, sheetId: string) => Promise<void>;
  updateGoal: (index: number, field: 'currentAmount', value: number, sheetId: string) => Promise<void>;

  // Add functions
  handleAddInvestment: (symbol: string, shares: number, costBasis: number, sheetId: string, paymentTier: PaymentTier, account?: string) => Promise<boolean>;
  handleAddBankAccount: (name: string, balance: number, sheetId: string, paymentTier: PaymentTier) => Promise<boolean>;
  handleAddCreditCard: (name: string, balance: number, limit: number, sheetId: string) => Promise<boolean>;
  handleAddAsset: (name: string, value: number, sheetId: string) => Promise<boolean>;
  handleAddHSA: (name: string, balance: number, sheetId: string) => Promise<boolean>;
  handleAddCreditScore: (bureau: string, score: number, sheetId: string) => Promise<boolean>;
  handleAddLiability: (name: string, value: number, interestRate: number, sheetId: string) => Promise<boolean>;
  handleAdd401kHolding: (fundName: string, ticker: string, shares: number, balance: number, employer: string, accountType: string, costBasis: number, sheetId: string, paymentTier: PaymentTier) => Promise<boolean>;
  handleBatchAddInvestments: (investments: Array<{ symbol: string; shares: number; costBasis: number; account?: string }>, sheetId: string) => Promise<boolean>;
  handleBatchAdd401kHoldings: (holdings: Array<{ fundName: string; ticker: string; shares: number; balance: number; employer: string; accountType: string; costBasis: number }>, sheetId: string) => Promise<boolean>;
  handleAddCrypto: (symbol: string, coinGeckoId: string, amount: number, costBasis: number, sheetId: string, paymentTier: PaymentTier, account?: string) => Promise<boolean>;
  handleAddPrivateEquity: (name: string, symbol: string, shares: number, pricePerShare: number, costBasis: number, sheetId: string, paymentTier: PaymentTier, account?: string) => Promise<boolean>;
  handleAddBudgetCategory: (category: string, budgeted: number, sheetId: string, group?: string) => Promise<boolean>;
  handleAddGoal: (name: string, targetAmount: number, currentAmount: number, targetDate: string, unit: string, sheetId: string) => Promise<boolean>;

  // Delete functions
  handleDeleteBankAccount: (index: number, sheetId: string) => Promise<void>;
  handleDeleteCreditCard: (index: number, sheetId: string) => Promise<void>;
  handleDeleteInvestment: (index: number, sheetId: string) => Promise<void>;
  handleDeleteAsset: (index: number, sheetId: string) => Promise<void>;
  handleDeleteHSA: (index: number, sheetId: string) => Promise<void>;
  handleDeleteCreditScore: (index: number, sheetId: string) => Promise<void>;
  handleDeleteLiability: (index: number, sheetId: string) => Promise<void>;
  handleDelete401kHolding: (index: number, sheetId: string) => Promise<void>;
  handleClearAll401kHoldings: (sheetId: string) => Promise<void>;
  handleDeleteCrypto: (index: number, sheetId: string) => Promise<void>;
  handleDeletePrivateEquity: (index: number, sheetId: string) => Promise<void>;
  handleDeleteBudgetCategory: (index: number, sheetId: string) => Promise<void>;
  handleDeleteGoal: (index: number, sheetId: string) => Promise<void>;
  handleBatchDelete: (sheetName: string, indices: number[], sheetId: string) => Promise<void>;

  // Tier-limited data
  getVisibleBankingData: (paymentTier: PaymentTier) => BankAccount[];
  getVisibleInvestments: (paymentTier: PaymentTier) => Investment[];
  getVisible401kHoldings: (paymentTier: PaymentTier) => Retirement401k[];
  getVisibleCrypto: (paymentTier: PaymentTier) => CryptoHolding[];
  getVisiblePrivateEquity: (paymentTier: PaymentTier) => PrivateEquity[];
  getVisibleNetWorthHistory: (paymentTier: PaymentTier) => NetWorthSnapshot[];
}

export interface CheckInEvent {
  id: number;
  streak: number;
  firstEver: boolean;
}

const FinancialDataContext = createContext<FinancialDataContextType | undefined>(undefined);

interface FinancialDataProviderProps {
  children: ReactNode;
}

export const FinancialDataProvider: React.FC<FinancialDataProviderProps> = ({ children }) => {
  const [bankingData, setBankingData] = useState<BankAccount[]>(DEFAULT_BANK_ACCOUNTS);
  const [creditCards, setCreditCards] = useState<CreditCard[]>(DEFAULT_CREDIT_CARDS);
  const [investments, setInvestments] = useState<Investment[]>([]);
  const [cryptoHoldings, setCryptoHoldings] = useState<CryptoHolding[]>([]);
  const [retirement401k, setRetirement401k] = useState<Retirement401k[]>([]);
  const [privateEquityHoldings, setPrivateEquityHoldings] = useState<PrivateEquity[]>([]);
  const [hsaAccounts, setHSAAccounts] = useState<HSA[]>([]);
  const [creditScores, setCreditScores] = useState<CreditScore[]>([]);
  const [marketSnapshot, setMarketSnapshot] = useState<MarketIndex[]>([]);
  const [retirementSettings, setRetirementSettings] = useState<RetirementSettings | null>(null);
  const [userSettings, setUserSettings] = useState<UserSettings>({ dashboardView: 'standard', hiddenWidgets: [] });
  const [assets, setAssets] = useState<Asset[]>(DEFAULT_ASSETS);
  const [liabilities, setLiabilities] = useState<Liability[]>(DEFAULT_LIABILITIES);
  const [budgetCategories, setBudgetCategories] = useState<BudgetCategory[]>([]);
  const [goals, setGoals] = useState<Goal[]>([]);
  const [netWorthHistory, setNetWorthHistory] = useState<NetWorthSnapshot[]>([]);
  const [checkIns, setCheckIns] = useState<CheckIn[]>([]);
  const checkInsRef = useRef<CheckIn[]>([]);
  const [checkInEvent, setCheckInEvent] = useState<CheckInEvent | null>(null);
  const [undoStack, setUndoStack] = useState<UndoAction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const lastSheetIdRef = useRef<string | null>(null);

  // Computed totals
  const totalBanking = calculateTotalBanking(bankingData);
  const { used: totalCreditUsed, limit: totalCreditLimit } = calculateTotalCredit(creditCards);
  const totalInvestmentValue = calculateTotalInvestments(investments);
  const totalCrypto = calculateTotalCrypto(cryptoHoldings);
  const total401k = calculateTotal401k(retirement401k);
  const totalPrivateEquity = calculateTotalPrivateEquity(privateEquityHoldings);
  const totalHSA = calculateTotalHSA(hsaAccounts);
  const totalAssets = calculateTotalAssets(assets, totalBanking, totalInvestmentValue, total401k, totalCrypto, totalPrivateEquity, totalHSA);
  const totalLiabilities = calculateTotalLiabilities(liabilities);
  const netWorth = calculateNetWorth(totalAssets, totalLiabilities + totalCreditUsed);
  const totalBudgeted = calculateTotalBudgeted(budgetCategories);
  const totalSpent = calculateTotalSpent(budgetCategories);
  const totalGoalsSaved = calculateTotalGoalsSaved(goals);
  const totalGoalsTarget = calculateTotalGoalsTarget(goals);

  // Load data from sheets
  const loadDataFromSheets = useCallback(async (sheetId: string): Promise<{ recreated: boolean; newSheetId?: string }> => {
    lastSheetIdRef.current = sheetId;
    setIsLoading(true);
    try {
      // Load data — ensureAllTabs inside loadFromSheets does the metadata fetch.
      // If the sheet was deleted (404), catch and recreate (Fix 4).
      let data: Awaited<ReturnType<typeof loadFromSheets>>;
      try {
        data = await loadFromSheets(sheetId);
      } catch (error: unknown) {
        const apiError = error as { status?: number; result?: { error?: { code?: number } } };
        const status = apiError.status || apiError.result?.error?.code;
        if (status === 404) {
          const currentData = {
            banking: bankingData,
            creditCards,
            investments,
            cryptoHoldings,
            retirement401k,
            privateEquityHoldings,
            hsaAccounts,
            assets,
            liabilities,
          };
          const newSheetId = await createSheetWithData(currentData);
          return { recreated: true, newSheetId };
        }
        throw error;
      }

      setBankingData(data.banking.length > 0 ? data.banking : []);
      setCreditCards(data.creditCards.length > 0 ? data.creditCards : []);
      setInvestments(data.investments);
      setRetirement401k(data.retirement401k);
      setPrivateEquityHoldings(data.privateEquityHoldings);
      setHSAAccounts(data.hsaAccounts);
      setCreditScores(data.creditScores);
      setMarketSnapshot(data.marketSnapshot);
      setRetirementSettings(data.retirementSettings);
      setUserSettings(data.userSettings);
      setAssets(data.assets.length > 0 ? data.assets : []);
      setLiabilities(data.liabilities.length > 0 ? data.liabilities : []);
      setBudgetCategories(data.budgetCategories);
      setGoals(data.goals);
      setNetWorthHistory(data.netWorthHistory);
      checkInsRef.current = data.checkIns;
      setCheckIns(data.checkIns);

      // Background: enrich investments with 1D/1W/1M/1Y price returns
      // (Sheets API cannot return historical GOOGLEFINANCE values)
      if (data.investments.length > 0) {
        const symbols = data.investments.map(inv => inv.symbol);
        fetchPeriodReturnsForSymbols(symbols).then(returnsMap => {
          setInvestments(prev =>
            prev.map(inv => {
              const ret = returnsMap[inv.symbol.toUpperCase()];
              if (!ret) return inv;
              return {
                ...inv,
                dailyReturn: ret.daily ?? inv.dailyReturn,
                weeklyReturn: ret.weekly,
                monthlyReturn: ret.monthly,
                annualReturn: ret.annual,
              };
            })
          );
        }).catch(err => {
          console.warn('Could not enrich investment period returns:', err);
        });
      }

      // Background: enrich PE holdings that have a public ticker with period returns
      const peSymbols = data.privateEquityHoldings
        .map(h => (h.symbol || '').trim().toUpperCase())
        .filter(s => s && s !== '-' && /^[A-Z0-9.-]{1,12}$/.test(s));
      if (peSymbols.length > 0) {
        fetchPeriodReturnsForSymbols(peSymbols).then(returnsMap => {
          setPrivateEquityHoldings(prev =>
            prev.map(h => {
              const sym = (h.symbol || '').trim().toUpperCase();
              const ret = returnsMap[sym];
              if (!ret) return h;
              return {
                ...h,
                dailyReturn: ret.daily,
                weeklyReturn: ret.weekly,
                monthlyReturn: ret.monthly,
                annualReturn: ret.annual,
              };
            })
          );
        }).catch(err => {
          console.warn('Could not enrich private equity period returns:', err);
        });
      }

      // Render now with last-known prices so net worth doesn't dip while live prices load
      const initialCrypto = applyCachedCryptoPrices(data.cryptoHoldings);
      setCryptoHoldings(initialCrypto);

      // Background: enrich crypto with live CoinGecko prices + period returns, then snapshot
      const today = new Date().toISOString().split('T')[0];
      const existingToday = data.netWorthHistory.find(s => s.date === today);
      // Every load refreshes today's row, so a bad early snapshot (e.g. prices down) self-heals.
      const recordTodaySnapshot = (snapshot: NetWorthSnapshot) => {
        if (existingToday && Math.abs(existingToday.netWorth - snapshot.netWorth) < 0.01) return;
        upsertNetWorthSnapshot(sheetId, snapshot)
          .then(() => setNetWorthHistory(prev => [...prev.filter(s => s.date !== snapshot.date), snapshot]))
          .catch(err => console.warn('Could not record net worth snapshot:', err));
      };

      if (data.cryptoHoldings.length > 0) {
        const ids = data.cryptoHoldings.map(h =>
          h.coinGeckoId || resolveSymbolToId(h.symbol) || ''
        ).filter(Boolean);
        const symbolById: Record<string, string> = {};
        data.cryptoHoldings.forEach(h => {
          const cgId = h.coinGeckoId || resolveSymbolToId(h.symbol) || '';
          if (cgId && h.symbol) symbolById[cgId] = h.symbol;
        });
        Promise.all([
          fetchCryptoPrices(ids, symbolById),
          fetchCryptoPeriodReturns(ids, symbolById),
        ]).then(([prices, periodReturns]) => {
          const enrichedCrypto = initialCrypto.map(h => {
            const cgId = h.coinGeckoId || resolveSymbolToId(h.symbol) || '';
            const priceData = prices[cgId];
            const periods = periodReturns[cgId];
            return {
              ...h,
              coinGeckoId: cgId || h.coinGeckoId,
              currentPrice: priceData ? priceData.usd : h.currentPrice,
              totalValue: priceData ? h.amount * priceData.usd : h.totalValue,
              change24h: priceData ? priceData.usd_24h_change : h.change24h,
              dailyReturn: periods?.daily ?? (priceData ? priceData.usd_24h_change : null),
              weeklyReturn: periods?.weekly ?? null,
              monthlyReturn: periods?.monthly ?? null,
              annualReturn: periods?.annual ?? null,
            };
          });
          setCryptoHoldings(enrichedCrypto);

          // Skip the snapshot if any coin is unpriced, or history records crypto at $0.
          const allPriced = ids.every(id => prices[id]);
          if (allPriced) {
            const freshBanking = calculateTotalBanking(data.banking);
            const freshCreditUsed = calculateTotalCredit(data.creditCards).used;
            const freshInvestments = calculateTotalInvestments(data.investments);
            const freshCrypto = calculateTotalCrypto(enrichedCrypto);
            const fresh401k = calculateTotal401k(data.retirement401k);
            const freshPrivateEquity = calculateTotalPrivateEquity(data.privateEquityHoldings);
            const freshHSA = calculateTotalHSA(data.hsaAccounts);
            const freshAssets = calculateTotalAssets(
              data.assets,
              freshBanking, freshInvestments, fresh401k, freshCrypto, freshPrivateEquity, freshHSA
            );
            const freshLiabilities = calculateTotalLiabilities(
              data.liabilities
            );
            const freshNetWorth = calculateNetWorth(freshAssets, freshLiabilities + freshCreditUsed);
            const snapshot = {
              date: today, netWorth: freshNetWorth, totalAssets: freshAssets,
              totalLiabilities: freshLiabilities, banking: freshBanking,
              investments: freshInvestments, retirement401k: fresh401k,
              creditCards: freshCreditUsed, crypto: freshCrypto,
            };
            recordTodaySnapshot(snapshot);
          }
        }).catch(err => {
          // No snapshot here: crypto would be recorded at $0. The next load retries.
          console.warn('CoinGecko enrichment failed:', err);
        });
      } else {
        // No crypto — record snapshot immediately (fire-and-forget)
        const freshBanking = calculateTotalBanking(data.banking.length > 0 ? data.banking : bankingData);
        const freshCreditUsed = calculateTotalCredit(data.creditCards.length > 0 ? data.creditCards : creditCards).used;
        const freshInvestments = calculateTotalInvestments(data.investments);
        const fresh401k = calculateTotal401k(data.retirement401k);
        const freshPrivateEquity = calculateTotalPrivateEquity(data.privateEquityHoldings);
        const freshHSA = calculateTotalHSA(data.hsaAccounts);
        const freshAssets = calculateTotalAssets(
          data.assets.length > 0 ? data.assets : assets,
          freshBanking, freshInvestments, fresh401k, 0, freshPrivateEquity, freshHSA
        );
        const freshLiabilities = calculateTotalLiabilities(
          data.liabilities.length > 0 ? data.liabilities : liabilities
        );
        const freshNetWorth = calculateNetWorth(freshAssets, freshLiabilities + freshCreditUsed);
        recordTodaySnapshot({
          date: today, netWorth: freshNetWorth, totalAssets: freshAssets,
          totalLiabilities: freshLiabilities, banking: freshBanking,
          investments: freshInvestments, retirement401k: fresh401k,
          creditCards: freshCreditUsed, crypto: 0,
        });
      }

      return { recreated: false };
    } finally {
      setIsLoading(false);
    }
  }, [bankingData, creditCards, investments, cryptoHoldings, retirement401k, privateEquityHoldings, hsaAccounts, assets, liabilities]);

  // Create new sheet
  const createNewSheet = useCallback(async () => {
    return await createTemplateSheet();
  }, []);

  // Weekly check-ins: one row per day in the user's sheet. Only user-initiated
  // edits and the "up to date" button count, never automatic writes like the
  // daily net worth snapshot.
  const recordCheckIn = useCallback((source: CheckIn['source']) => {
    const sheetId = lastSheetIdRef.current;
    if (!sheetId) return;
    const date = toDateKey(new Date());
    const previous = checkInsRef.current;
    if (previous.some(c => c.date === date)) return;

    const entry: CheckIn = { date, source };
    const next = [...previous, entry];
    checkInsRef.current = next;
    setCheckIns(next);
    appendCheckIn(sheetId, entry).catch(err => console.warn('Failed to record check-in:', err));

    if (!summarizeStreak(previous).checkedInThisWeek) {
      setCheckInEvent({ id: Date.now(), streak: summarizeStreak(next).current, firstEver: previous.length === 0 });
    }
  }, []);

  const confirmUpToDate = useCallback(() => recordCheckIn('confirm'), [recordCheckIn]);
  const dismissCheckInEvent = useCallback(() => setCheckInEvent(null), []);

  // Cached per function so wrapped callbacks keep a stable identity between renders.
  const trackedFnsRef = useRef(new WeakMap<object, unknown>());
  const trackEdit = <A extends unknown[], R>(fn: (...args: A) => Promise<R>): ((...args: A) => Promise<R>) => {
    const cached = trackedFnsRef.current.get(fn) as ((...args: A) => Promise<R>) | undefined;
    if (cached) return cached;
    const wrapped = async (...args: A): Promise<R> => {
      const result = await fn(...args);
      if (result !== false) recordCheckIn('edit');
      return result;
    };
    trackedFnsRef.current.set(fn, wrapped);
    return wrapped;
  };

  // Add to undo stack
  const pushToUndoStack = useCallback(
    (type: EntityType, index: number, field: string, cell: string, oldValue: number | string, newValue: number | string) => {
      setUndoStack(prev => [
        ...prev,
        { type, index, field, cell, oldValue, newValue, timestamp: Date.now() },
      ]);
    },
    []
  );

  // Undo last change
  const handleUndo = useCallback(async () => {
    if (undoStack.length === 0) return;

    const lastChange = undoStack[undoStack.length - 1];
    const sheetId = lastSheetIdRef.current;
    if (!sheetId) return;

    await updateSheetValue(sheetId, lastChange.cell, lastChange.oldValue);

    switch (lastChange.type) {
      case 'banking':
        setBankingData(prev => {
          const newData = [...prev];
          newData[lastChange.index].balance = lastChange.oldValue as number;
          return newData;
        });
        break;
      case 'creditCard':
        setCreditCards(prev => {
          const newData = [...prev];
          if (lastChange.field === 'balance') {
            newData[lastChange.index].balance = lastChange.oldValue as number;
          } else if (lastChange.field === 'limit') {
            newData[lastChange.index].limit = lastChange.oldValue as number;
          }
          return newData;
        });
        break;
      case 'hsa':
        setHSAAccounts(prev => {
          const newData = [...prev];
          newData[lastChange.index].balance = lastChange.oldValue as number;
          return newData;
        });
        break;
      case 'asset':
        setAssets(prev => {
          const newData = [...prev];
          newData[lastChange.index].value = lastChange.oldValue as number;
          return newData;
        });
        break;
      case 'liability':
        setLiabilities(prev => {
          const newData = [...prev];
          if (lastChange.field === 'interestRate') {
            newData[lastChange.index] = { ...newData[lastChange.index], interestRate: lastChange.oldValue as number };
          } else {
            newData[lastChange.index] = { ...newData[lastChange.index], value: lastChange.oldValue as number };
          }
          return newData;
        });
        break;
      case 'retirement401k':
        setRetirement401k(prev => {
          const newData = [...prev];
          if (lastChange.field === 'balance') {
            newData[lastChange.index].balance = lastChange.oldValue as number;
          } else if (lastChange.field === 'shares') {
            newData[lastChange.index].shares = lastChange.oldValue as number;
          } else if (lastChange.field === 'costBasis') {
            newData[lastChange.index].costBasis = lastChange.oldValue as number;
          } else if (lastChange.field === 'fundName') {
            newData[lastChange.index] = { ...newData[lastChange.index], fundName: lastChange.oldValue as string };
          } else if (lastChange.field === 'ticker') {
            newData[lastChange.index] = { ...newData[lastChange.index], ticker: lastChange.oldValue as string };
          }
          return newData;
        });
        break;
      case 'privateEquity':
        setPrivateEquityHoldings(prev => {
          const newData = [...prev];
          if (lastChange.field === 'shares') {
            const shares = lastChange.oldValue as number;
            newData[lastChange.index] = { ...newData[lastChange.index], shares, totalValue: shares * newData[lastChange.index].pricePerShare };
          } else if (lastChange.field === 'pricePerShare') {
            const pricePerShare = lastChange.oldValue as number;
            newData[lastChange.index] = { ...newData[lastChange.index], pricePerShare, totalValue: newData[lastChange.index].shares * pricePerShare };
          } else if (lastChange.field === 'costBasis') {
            newData[lastChange.index] = { ...newData[lastChange.index], costBasis: lastChange.oldValue as number };
          } else if (lastChange.field === 'name') {
            newData[lastChange.index] = { ...newData[lastChange.index], name: lastChange.oldValue as string };
          } else if (lastChange.field === 'symbol') {
            newData[lastChange.index] = { ...newData[lastChange.index], symbol: lastChange.oldValue as string };
          }
          return newData;
        });
        break;
      case 'investment':
        setInvestments(prev => {
          const newData = [...prev];
          if (lastChange.field === 'symbol') {
            newData[lastChange.index] = { ...newData[lastChange.index], symbol: lastChange.oldValue as string, name: lastChange.oldValue as string };
          } else if (lastChange.field === 'shares') {
            newData[lastChange.index] = { ...newData[lastChange.index], shares: lastChange.oldValue as number };
          } else if (lastChange.field === 'costBasis') {
            newData[lastChange.index] = { ...newData[lastChange.index], costBasis: lastChange.oldValue as number };
          }
          return newData;
        });
        break;
      case 'crypto':
        setCryptoHoldings(prev => {
          const newData = [...prev];
          if (lastChange.field === 'symbol') {
            newData[lastChange.index] = { ...newData[lastChange.index], symbol: lastChange.oldValue as string };
          } else if (lastChange.field === 'amount') {
            newData[lastChange.index] = { ...newData[lastChange.index], amount: lastChange.oldValue as number };
          } else if (lastChange.field === 'costBasis') {
            newData[lastChange.index] = { ...newData[lastChange.index], costBasis: lastChange.oldValue as number };
          }
          return newData;
        });
        break;
      case 'budgetCategory':
        setBudgetCategories(prev => {
          const newData = [...prev];
          if (lastChange.field === 'budgeted') {
            newData[lastChange.index].budgeted = lastChange.oldValue as number;
          } else if (lastChange.field === 'spent') {
            newData[lastChange.index].spent = lastChange.oldValue as number;
          }
          return newData;
        });
        break;
      case 'goal':
        setGoals(prev => {
          const newData = [...prev];
          if (lastChange.field === 'currentAmount') {
            newData[lastChange.index].currentAmount = lastChange.oldValue as number;
          }
          return newData;
        });
        break;
    }

    setUndoStack(prev => prev.slice(0, -1));
  }, [undoStack]);

  // Update banking data
  const updateBankingData = useCallback(
    async (index: number, balance: number, sheetId: string) => {
      const oldValue = bankingData[index].balance;
      const cell = bankingData[index].cell;

      setBankingData(prev => {
        const newData = [...prev];
        newData[index] = { ...newData[index], balance };
        return newData;
      });

      await updateSheetValue(sheetId, cell, balance);
      pushToUndoStack('banking', index, 'balance', cell, oldValue, balance);
    },
    [bankingData, pushToUndoStack]
  );

  // Update credit card
  const updateCreditCard = useCallback(
    async (index: number, field: 'balance' | 'limit', value: number, sheetId: string) => {
      const card = creditCards[index];
      const oldValue = field === 'balance' ? card.balance : card.limit;
      const cell = field === 'balance' ? card.balanceCell : card.limitCell;

      setCreditCards(prev => {
        const newData = [...prev];
        if (field === 'balance') {
          newData[index] = { ...newData[index], balance: value };
        } else {
          newData[index] = { ...newData[index], limit: value };
        }
        return newData;
      });

      await updateSheetValue(sheetId, cell, value);
      pushToUndoStack('creditCard', index, field, cell, oldValue, value);
    },
    [creditCards, pushToUndoStack]
  );

  // Update asset
  const updateAsset = useCallback(
    async (index: number, value: number, sheetId: string) => {
      const oldValue = assets[index].value;
      const cell = assets[index].cell;

      setAssets(prev => {
        const newData = [...prev];
        newData[index] = { ...newData[index], value };
        return newData;
      });

      await updateSheetValue(sheetId, cell, value);
      pushToUndoStack('asset', index, 'value', cell, oldValue, value);
    },
    [assets, pushToUndoStack]
  );

  // Update HSA
  const updateHSA = useCallback(
    async (index: number, balance: number, sheetId: string) => {
      const oldValue = hsaAccounts[index].balance;
      const cell = hsaAccounts[index].cell;

      setHSAAccounts(prev => {
        const newData = [...prev];
        newData[index] = { ...newData[index], balance };
        return newData;
      });

      await updateSheetValue(sheetId, cell, balance);
      pushToUndoStack('hsa', index, 'balance', cell, oldValue, balance);
    },
    [hsaAccounts, pushToUndoStack]
  );

  const updateCreditScore = useCallback(
    async (index: number, score: number, sheetId: string) => {
      const oldValue = creditScores[index].score;
      const cell = creditScores[index].scoreCell;

      setCreditScores(prev => {
        const newData = [...prev];
        newData[index] = { ...newData[index], score };
        return newData;
      });

      await updateSheetValue(sheetId, cell, score);
      pushToUndoStack('creditScore', index, 'score', cell, oldValue, score);
    },
    [creditScores, pushToUndoStack]
  );

  // Update liability
  const updateLiability = useCallback(
    async (index: number, field: 'value' | 'interestRate', value: number, sheetId: string) => {
      const liability = liabilities[index];
      const cellMap = { value: liability.cell, interestRate: liability.interestRateCell };
      const oldValueMap = { value: liability.value, interestRate: liability.interestRate };
      const cell = cellMap[field];
      const oldValue = oldValueMap[field];

      setLiabilities(prev => {
        const newData = [...prev];
        newData[index] = { ...newData[index], [field]: value };
        return newData;
      });

      await updateSheetValue(sheetId, cell, value);
      pushToUndoStack('liability', index, field, cell, oldValue, value);
    },
    [liabilities, pushToUndoStack]
  );

  // Update 401k holding
  const update401kHolding = useCallback(
    async (index: number, field: 'balance' | 'shares' | 'costBasis' | 'fundName' | 'ticker', value: number | string, sheetId: string) => {
      const holding = retirement401k[index];
      const cellMap: Record<string, string> = { balance: holding.balanceCell, shares: holding.sharesCell, costBasis: holding.costBasisCell, fundName: holding.fundNameCell, ticker: holding.tickerCell };
      const oldValueMap: Record<string, number | string> = { balance: holding.balance, shares: holding.shares, costBasis: holding.costBasis, fundName: holding.fundName, ticker: holding.ticker };
      const cell = cellMap[field];
      const oldValue = oldValueMap[field];

      setRetirement401k(prev => {
        const newData = [...prev];
        newData[index] = { ...newData[index], [field]: value };
        return newData;
      });

      await updateSheetValue(sheetId, cell, value);
      pushToUndoStack('retirement401k', index, field, cell, oldValue, value);
    },
    [retirement401k, pushToUndoStack]
  );

  // Update private equity holding
  const updatePrivateEquityHolding = useCallback(
    async (index: number, field: 'shares' | 'pricePerShare' | 'costBasis' | 'name' | 'symbol', value: number | string, sheetId: string) => {
      const holding = privateEquityHoldings[index];
      const cellMap: Record<string, string> = { shares: holding.sharesCell, pricePerShare: holding.pricePerShareCell, costBasis: holding.costBasisCell, name: holding.nameCell, symbol: holding.symbolCell };
      const oldValueMap: Record<string, number | string> = { shares: holding.shares, pricePerShare: holding.pricePerShare, costBasis: holding.costBasis, name: holding.name, symbol: holding.symbol };
      const cell = cellMap[field];
      const oldValue = oldValueMap[field];

      setPrivateEquityHoldings(prev => {
        const newData = [...prev];
        const updated = { ...newData[index], [field]: value };
        // Recalculate totalValue for numeric fields
        if (field === 'shares' || field === 'pricePerShare') {
          updated.totalValue = updated.shares * updated.pricePerShare;
        }
        newData[index] = updated;
        return newData;
      });

      await updateSheetValue(sheetId, cell, value);
      pushToUndoStack('privateEquity', index, field, cell, oldValue, value);

      // Also update the Total Value cell in the sheet
      if (field === 'shares' || field === 'pricePerShare') {
        const updatedHolding = { ...holding, [field]: value as number };
        const newTotal = updatedHolding.shares * updatedHolding.pricePerShare;
        const totalValueCell = `'Private Equity'!E${index + 2}`;
        await updateSheetValue(sheetId, totalValueCell, newTotal);
      }
    },
    [privateEquityHoldings, pushToUndoStack]
  );

  // Update investment
  const updateInvestment = useCallback(
    async (index: number, field: 'symbol' | 'shares' | 'costBasis', value: number | string, sheetId: string) => {
      const inv = investments[index];
      const cellMap: Record<string, string> = { symbol: inv.symbolCell, shares: inv.sharesCell, costBasis: inv.costBasisCell };
      const oldValueMap: Record<string, number | string> = { symbol: inv.symbol, shares: inv.shares, costBasis: inv.costBasis };
      const cell = cellMap[field];
      const oldValue = oldValueMap[field];

      setInvestments(prev => {
        const newData = [...prev];
        if (field === 'symbol') {
          newData[index] = { ...newData[index], symbol: value as string, name: value as string };
        } else {
          newData[index] = { ...newData[index], [field]: value };
        }
        return newData;
      });

      await updateSheetValue(sheetId, cell, value);
      pushToUndoStack('investment', index, field, cell, oldValue, value);
    },
    [investments, pushToUndoStack]
  );

  // Update crypto holding
  const updateCryptoHolding = useCallback(
    async (index: number, field: 'symbol' | 'amount' | 'costBasis', value: number | string, sheetId: string) => {
      const holding = cryptoHoldings[index];
      const cellMap: Record<string, string> = { symbol: holding.symbolCell, amount: holding.amountCell, costBasis: holding.costBasisCell };
      const oldValueMap: Record<string, number | string> = { symbol: holding.symbol, amount: holding.amount, costBasis: holding.costBasis };
      const cell = cellMap[field];
      const oldValue = oldValueMap[field];

      setCryptoHoldings(prev => {
        const newData = [...prev];
        newData[index] = { ...newData[index], [field]: value };
        return newData;
      });

      await updateSheetValue(sheetId, cell, value);
      pushToUndoStack('crypto', index, field, cell, oldValue, value);
    },
    [cryptoHoldings, pushToUndoStack]
  );

  // Add investment
  const handleAddInvestment = useCallback(
    async (symbol: string, shares: number, costBasis: number, sheetId: string, paymentTier: PaymentTier, account: string = 'Individual'): Promise<boolean> => {
      if (paymentTier === 'free' && investments.length >= TIER_LIMITS.free.investments) {
        return false; // Triggers upgrade modal
      }

      const nextRow = investments.length + 2;
      await addInvestment(sheetId, symbol.toUpperCase(), shares, costBasis, nextRow, account);
      await loadDataFromSheets(sheetId);
      return true;
    },
    [investments.length, loadDataFromSheets]
  );

  // Add bank account
  const handleAddBankAccount = useCallback(
    async (name: string, balance: number, sheetId: string, paymentTier: PaymentTier): Promise<boolean> => {
      if (paymentTier === 'free' && bankingData.length >= TIER_LIMITS.free.bankAccounts) {
        return false; // Triggers upgrade modal
      }

      const nextRow = bankingData.length + 2;
      await addBankAccount(sheetId, name, balance, nextRow);
      await loadDataFromSheets(sheetId);
      return true;
    },
    [bankingData.length, loadDataFromSheets]
  );

  // Add credit card
  const handleAddCreditCard = useCallback(
    async (name: string, balance: number, limit: number, sheetId: string): Promise<boolean> => {
      const nextRow = creditCards.length + 2;
      await addCreditCard(sheetId, name, balance, limit, nextRow);
      await loadDataFromSheets(sheetId);
      return true;
    },
    [creditCards.length, loadDataFromSheets]
  );

  // Add asset
  const handleAddAsset = useCallback(
    async (name: string, value: number, sheetId: string): Promise<boolean> => {
      const nextRow = assets.length + 2;
      await addAsset(sheetId, name, value, nextRow);
      await loadDataFromSheets(sheetId);
      return true;
    },
    [assets.length, loadDataFromSheets]
  );

  // Add HSA account
  const handleAddHSA = useCallback(
    async (name: string, balance: number, sheetId: string): Promise<boolean> => {
      const nextRow = hsaAccounts.length + 2;
      await addHSA(sheetId, name, balance, nextRow);
      await loadDataFromSheets(sheetId);
      return true;
    },
    [hsaAccounts.length, loadDataFromSheets]
  );

  const handleAddCreditScore = useCallback(
    async (bureau: string, score: number, sheetId: string): Promise<boolean> => {
      const nextRow = creditScores.length + 2;
      await addCreditScore(sheetId, bureau, score, nextRow);
      await loadDataFromSheets(sheetId);
      return true;
    },
    [creditScores.length, loadDataFromSheets]
  );

  // Add liability
  const handleAddLiability = useCallback(
    async (name: string, value: number, interestRate: number, sheetId: string): Promise<boolean> => {
      const nextRow = liabilities.length + 2;
      await addLiability(sheetId, name, value, interestRate, nextRow);
      await loadDataFromSheets(sheetId);
      return true;
    },
    [liabilities.length, loadDataFromSheets]
  );

  // Add 401k holding
  const handleAdd401kHolding = useCallback(
    async (
      fundName: string, ticker: string, shares: number, balance: number,
      employer: string, accountType: string, costBasis: number,
      sheetId: string, paymentTier: PaymentTier
    ): Promise<boolean> => {
      if (paymentTier === 'free' && retirement401k.length >= TIER_LIMITS.free.retirement401kHoldings) {
        return false;
      }

      const nextRow = retirement401k.length + 2;
      await add401kHolding(sheetId, fundName, ticker, shares, balance, employer, accountType, costBasis, nextRow);
      await loadDataFromSheets(sheetId);
      return true;
    },
    [retirement401k.length, loadDataFromSheets]
  );

  // Add crypto holding
  const handleAddCrypto = useCallback(
    async (symbol: string, coinGeckoId: string, amount: number, costBasis: number, sheetId: string, paymentTier: PaymentTier, account: string = 'Main'): Promise<boolean> => {
      if (paymentTier === 'free' && cryptoHoldings.length >= TIER_LIMITS.free.cryptoHoldings) {
        return false;
      }

      // Auto-resolve CoinGecko ID if not provided
      const resolvedId = coinGeckoId || resolveSymbolToId(symbol) || '';

      const nextRow = cryptoHoldings.length + 2;
      await addCryptoHolding(sheetId, symbol, resolvedId, amount, costBasis, nextRow, account);
      await loadDataFromSheets(sheetId);
      return true;
    },
    [cryptoHoldings.length, loadDataFromSheets]
  );

  // Add private equity holding
  const handleAddPrivateEquity = useCallback(
    async (name: string, symbol: string, shares: number, pricePerShare: number, costBasis: number, sheetId: string, paymentTier: PaymentTier, account: string = 'Individual'): Promise<boolean> => {
      if (paymentTier === 'free' && privateEquityHoldings.length >= TIER_LIMITS.free.privateEquityHoldings) {
        return false;
      }

      const nextRow = privateEquityHoldings.length + 2;
      await addPrivateEquityHolding(sheetId, name, symbol, shares, pricePerShare, costBasis, nextRow, account);
      await loadDataFromSheets(sheetId);
      return true;
    },
    [privateEquityHoldings.length, loadDataFromSheets]
  );

  // Batch add investments (CSV import) — merges with existing holdings by symbol+account
  const handleBatchAddInvestments = useCallback(
    async (
      investmentRows: Array<{ symbol: string; shares: number; costBasis: number; account?: string }>,
      sheetId: string
    ): Promise<boolean> => {
      // Build lookup of existing investments by symbol+account → sheet row
      const existingMap = new Map<string, { row: number; inv: Investment }>();
      investments.forEach((inv, idx) => {
        const key = `${inv.symbol.toUpperCase()}|${(inv.account || 'Individual').toLowerCase()}`;
        existingMap.set(key, { row: idx + 2, inv });
      });

      const toUpdate: Array<{ row: number; shares: number; costBasis: number }> = [];
      const toAdd: Array<{ symbol: string; shares: number; costBasis: number; account?: string }> = [];

      for (const row of investmentRows) {
        const key = `${row.symbol.toUpperCase()}|${(row.account || 'Individual').toLowerCase()}`;
        const existing = existingMap.get(key);
        if (existing) {
          // Update existing row in-place with new data
          toUpdate.push({ row: existing.row, shares: row.shares, costBasis: row.costBasis });
        } else {
          toAdd.push(row);
        }
      }

      // Update existing rows
      if (toUpdate.length > 0) {
        await batchUpdateInvestments(sheetId, toUpdate);
      }
      // Add new rows
      if (toAdd.length > 0) {
        const startRow = investments.length + 2;
        await batchAddInvestments(sheetId, toAdd, startRow);
      }

      await loadDataFromSheets(sheetId);
      return true;
    },
    [investments, loadDataFromSheets]
  );

  // Batch add 401k holdings (CSV import)
  const handleBatchAdd401kHoldings = useCallback(
    async (
      holdings: Array<{ fundName: string; ticker: string; shares: number; balance: number; employer: string; accountType: string; costBasis: number }>,
      sheetId: string
    ): Promise<boolean> => {
      const startRow = retirement401k.length + 2;
      await batchAdd401kHoldings(sheetId, holdings, startRow);
      await loadDataFromSheets(sheetId);
      return true;
    },
    [retirement401k.length, loadDataFromSheets]
  );

  // Delete bank account
  const handleDeleteBankAccount = useCallback(
    async (index: number, sheetId: string): Promise<void> => {
      const rowIndex = index + 2; // +2 for header row and 1-based index
      await deleteRow(sheetId, 'Banking', rowIndex);
      setUndoStack([]); // Row deletion invalidates cell references in undo stack
      await loadDataFromSheets(sheetId);
    },
    [loadDataFromSheets]
  );

  // Delete credit card
  const handleDeleteCreditCard = useCallback(
    async (index: number, sheetId: string): Promise<void> => {
      const rowIndex = index + 2;
      await deleteRow(sheetId, 'Credit Cards', rowIndex);
      setUndoStack([]);
      await loadDataFromSheets(sheetId);
    },
    [loadDataFromSheets]
  );

  // Delete investment
  const handleDeleteInvestment = useCallback(
    async (index: number, sheetId: string): Promise<void> => {
      const rowIndex = index + 2;
      await deleteRow(sheetId, 'Investments', rowIndex);
      setUndoStack([]);
      await loadDataFromSheets(sheetId);
    },
    [loadDataFromSheets]
  );

  // Delete asset
  const handleDeleteAsset = useCallback(
    async (index: number, sheetId: string): Promise<void> => {
      const rowIndex = index + 2;
      await deleteRow(sheetId, 'Assets', rowIndex);
      setUndoStack([]);
      await loadDataFromSheets(sheetId);
    },
    [loadDataFromSheets]
  );

  // Delete HSA account
  const handleDeleteHSA = useCallback(
    async (index: number, sheetId: string): Promise<void> => {
      const rowIndex = index + 2;
      await deleteRow(sheetId, 'HSA', rowIndex);
      setUndoStack([]);
      await loadDataFromSheets(sheetId);
    },
    [loadDataFromSheets]
  );

  const handleDeleteCreditScore = useCallback(
    async (index: number, sheetId: string): Promise<void> => {
      const rowIndex = index + 2;
      await deleteRow(sheetId, 'Credit Scores', rowIndex);
      setUndoStack([]);
      await loadDataFromSheets(sheetId);
    },
    [loadDataFromSheets]
  );

  // Add market ticker — returns price (0 = invalid ticker)
  const handleAddMarketTicker = useCallback(
    async (symbol: string, label: string, sheetId: string): Promise<number> => {
      const nextRow = marketSnapshot.length + 2;
      const price = await addMarketTicker(sheetId, symbol, label, nextRow);
      if (price > 0) {
        await loadDataFromSheets(sheetId);
      } else {
        // Invalid ticker — remove the row we just added
        await deleteRow(sheetId, 'Market Snapshot', nextRow);
      }
      return price;
    },
    [marketSnapshot.length, loadDataFromSheets]
  );

  // Delete market ticker
  const handleDeleteMarketTicker = useCallback(
    async (index: number, sheetId: string): Promise<void> => {
      const rowIndex = index + 2;
      await deleteRow(sheetId, 'Market Snapshot', rowIndex);
      await loadDataFromSheets(sheetId);
    },
    [loadDataFromSheets]
  );

  // Delete liability
  const handleDeleteLiability = useCallback(
    async (index: number, sheetId: string): Promise<void> => {
      const rowIndex = index + 2;
      await deleteRow(sheetId, 'Liabilities', rowIndex);
      setUndoStack([]);
      await loadDataFromSheets(sheetId);
    },
    [loadDataFromSheets]
  );

  // Delete 401k holding
  const handleDelete401kHolding = useCallback(
    async (index: number, sheetId: string): Promise<void> => {
      const rowIndex = index + 2;
      await deleteRow(sheetId, '401k', rowIndex);
      setUndoStack([]);
      await loadDataFromSheets(sheetId);
    },
    [loadDataFromSheets]
  );

  // Delete crypto holding
  const handleDeleteCrypto = useCallback(
    async (index: number, sheetId: string): Promise<void> => {
      const rowIndex = index + 2;
      await deleteRow(sheetId, 'Crypto', rowIndex);
      setUndoStack([]);
      await loadDataFromSheets(sheetId);
    },
    [loadDataFromSheets]
  );

  // Delete private equity holding
  const handleDeletePrivateEquity = useCallback(
    async (index: number, sheetId: string): Promise<void> => {
      const rowIndex = index + 2;
      await deleteRow(sheetId, 'Private Equity', rowIndex);
      setUndoStack([]);
      await loadDataFromSheets(sheetId);
    },
    [loadDataFromSheets]
  );

  // Clear all 401k holdings
  const handleClearAll401kHoldings = useCallback(
    async (sheetId: string): Promise<void> => {
      await clearAll401kHoldings(sheetId);
      setUndoStack([]);
      setRetirement401k([]);
    },
    []
  );

  // Update budget category
  const updateBudgetCategory = useCallback(
    async (index: number, field: 'budgeted' | 'spent', value: number, sheetId: string) => {
      const cat = budgetCategories[index];
      const oldValue = field === 'budgeted' ? cat.budgeted : cat.spent;
      const cell = field === 'budgeted' ? cat.cell : cat.spentCell;

      await updateSheetValue(sheetId, cell, value);
      pushToUndoStack('budgetCategory', index, field, cell, oldValue, value);

      setBudgetCategories(prev => {
        const newData = [...prev];
        newData[index] = { ...newData[index], [field]: value };
        return newData;
      });
    },
    [budgetCategories, pushToUndoStack]
  );

  // Update goal
  const updateGoal = useCallback(
    async (index: number, field: 'currentAmount', value: number, sheetId: string) => {
      const goal = goals[index];
      const oldValue = goal.currentAmount;
      const cell = goal.currentAmountCell;

      await updateSheetValue(sheetId, cell, value);
      pushToUndoStack('goal', index, field, cell, oldValue, value);

      setGoals(prev => {
        const newData = [...prev];
        newData[index] = { ...newData[index], currentAmount: value };
        return newData;
      });
    },
    [goals, pushToUndoStack]
  );

  // Add budget category
  const handleAddBudgetCategory = useCallback(
    async (category: string, budgeted: number, sheetId: string, group?: string): Promise<boolean> => {
      const nextRow = budgetCategories.length + 2;
      await addBudgetCategoryToSheet(sheetId, category, budgeted, nextRow, group);
      await loadDataFromSheets(sheetId);
      return true;
    },
    [budgetCategories.length, loadDataFromSheets]
  );

  // Add goal
  const handleAddGoal = useCallback(
    async (name: string, targetAmount: number, currentAmount: number, targetDate: string, unit: string, sheetId: string): Promise<boolean> => {
      const nextRow = goals.length + 2;
      await addGoalToSheet(sheetId, name, targetAmount, currentAmount, targetDate, unit, nextRow);
      await loadDataFromSheets(sheetId);
      return true;
    },
    [goals.length, loadDataFromSheets]
  );

  // Delete budget category
  const handleDeleteBudgetCategory = useCallback(
    async (index: number, sheetId: string): Promise<void> => {
      const rowIndex = index + 2;
      await deleteRow(sheetId, 'Budget', rowIndex);
      setUndoStack([]);
      await loadDataFromSheets(sheetId);
    },
    [loadDataFromSheets]
  );

  // Delete goal
  const handleDeleteGoal = useCallback(
    async (index: number, sheetId: string): Promise<void> => {
      const rowIndex = index + 2;
      await deleteRow(sheetId, 'Goals', rowIndex);
      setUndoStack([]);
      await loadDataFromSheets(sheetId);
    },
    [loadDataFromSheets]
  );

  // Batch delete multiple rows at once (single API call + single reload)
  const handleBatchDelete = useCallback(
    async (sheetName: string, indices: number[], sheetId: string): Promise<void> => {
      setIsLoading(true);
      try {
        const rowIndices = indices.map(i => i + 2);
        await deleteRows(sheetId, sheetName, rowIndices);
        setUndoStack([]);
        await loadDataFromSheets(sheetId);
      } finally {
        setIsLoading(false);
      }
    },
    [loadDataFromSheets]
  );

  // Get visible data based on tier
  const getVisibleBankingData = useCallback(
    (paymentTier: PaymentTier) => {
      return paymentTier === 'free'
        ? bankingData.slice(0, TIER_LIMITS.free.bankAccounts)
        : bankingData;
    },
    [bankingData]
  );

  const getVisibleInvestments = useCallback(
    (paymentTier: PaymentTier) => {
      return paymentTier === 'free'
        ? investments.slice(0, TIER_LIMITS.free.investments)
        : investments;
    },
    [investments]
  );

  const getVisible401kHoldings = useCallback(
    (paymentTier: PaymentTier) => {
      return paymentTier === 'free'
        ? retirement401k.slice(0, TIER_LIMITS.free.retirement401kHoldings)
        : retirement401k;
    },
    [retirement401k]
  );

  const getVisibleCrypto = useCallback(
    (paymentTier: PaymentTier) => {
      return paymentTier === 'free'
        ? cryptoHoldings.slice(0, TIER_LIMITS.free.cryptoHoldings)
        : cryptoHoldings;
    },
    [cryptoHoldings]
  );

  const getVisiblePrivateEquity = useCallback(
    (paymentTier: PaymentTier) => {
      return paymentTier === 'free'
        ? privateEquityHoldings.slice(0, TIER_LIMITS.free.privateEquityHoldings)
        : privateEquityHoldings;
    },
    [privateEquityHoldings]
  );

  const getVisibleNetWorthHistory = useCallback(
    (paymentTier: PaymentTier) => {
      const months = TIER_LIMITS[paymentTier].historyMonths;
      if (!Number.isFinite(months)) return netWorthHistory;
      // Display-only truncation. Nothing is removed from the sheet, so the full
      // history reappears the moment someone upgrades — and survives a lapse
      // back to Free.
      const cutoff = new Date();
      cutoff.setMonth(cutoff.getMonth() - months);
      const cutoffDate = cutoff.toISOString().slice(0, 10);
      return netWorthHistory.filter(snapshot => snapshot.date >= cutoffDate);
    },
    [netWorthHistory]
  );

  const updateDashboardView = useCallback(async (view: DashboardView, sheetId: string) => {
    setUserSettings(prev => ({ ...prev, dashboardView: view }));
    try {
      await saveUserSettings(sheetId, { ...userSettings, dashboardView: view });
    } catch (err) {
      console.warn('Failed to save dashboard view:', err);
    }
  }, [userSettings]);

  const toggleWidgetVisibility = useCallback(async (widget: WidgetKey, sheetId: string) => {
    setUserSettings(prev => {
      const hidden = prev.hiddenWidgets || [];
      const isHidden = hidden.includes(widget);
      const updated = isHidden ? hidden.filter(w => w !== widget) : [...hidden, widget];
      const next = { ...prev, hiddenWidgets: updated };
      saveUserSettings(sheetId, next).catch(err => console.warn('Failed to save widget visibility:', err));
      return next;
    });
  }, []);

  return (
    <FinancialDataContext.Provider
      value={{
        bankingData,
        creditCards,
        investments,
        cryptoHoldings,
        retirement401k,
        privateEquityHoldings,
        hsaAccounts,
        creditScores,
        marketSnapshot,
        retirementSettings,
        updateRetirementSettings: setRetirementSettings,
        userSettings,
        updateDashboardView,
        toggleWidgetVisibility,
        handleAddMarketTicker,
        handleDeleteMarketTicker,
        assets,
        liabilities,
        budgetCategories,
        goals,
        netWorthHistory,
        getVisibleNetWorthHistory,
        checkIns,
        checkInEvent,
        dismissCheckInEvent,
        confirmUpToDate,
        totalBanking,
        totalCreditUsed,
        totalCreditLimit,
        totalInvestmentValue,
        totalCrypto,
        total401k,
        totalPrivateEquity,
        totalHSA,
        totalAssets,
        totalLiabilities,
        netWorth,
        totalBudgeted,
        totalSpent,
        totalGoalsSaved,
        totalGoalsTarget,
        undoStack,
        handleUndo,
        isLoading,
        loadDataFromSheets,
        createNewSheet,
        updateBankingData: trackEdit(updateBankingData),
        updateCreditCard: trackEdit(updateCreditCard),
        updateAsset: trackEdit(updateAsset),
        updateHSA: trackEdit(updateHSA),
        updateCreditScore: trackEdit(updateCreditScore),
        updateLiability: trackEdit(updateLiability),
        updateInvestment: trackEdit(updateInvestment),
        updateCryptoHolding: trackEdit(updateCryptoHolding),
        update401kHolding: trackEdit(update401kHolding),
        updatePrivateEquityHolding: trackEdit(updatePrivateEquityHolding),
        updateBudgetCategory: trackEdit(updateBudgetCategory),
        updateGoal: trackEdit(updateGoal),
        handleAddInvestment: trackEdit(handleAddInvestment),
        handleAddBankAccount: trackEdit(handleAddBankAccount),
        handleAddCreditCard: trackEdit(handleAddCreditCard),
        handleAddAsset: trackEdit(handleAddAsset),
        handleAddHSA: trackEdit(handleAddHSA),
        handleAddCreditScore: trackEdit(handleAddCreditScore),
        handleAddLiability: trackEdit(handleAddLiability),
        handleAdd401kHolding: trackEdit(handleAdd401kHolding),
        handleAddCrypto: trackEdit(handleAddCrypto),
        handleAddPrivateEquity: trackEdit(handleAddPrivateEquity),
        handleBatchAddInvestments: trackEdit(handleBatchAddInvestments),
        handleBatchAdd401kHoldings: trackEdit(handleBatchAdd401kHoldings),
        handleAddBudgetCategory: trackEdit(handleAddBudgetCategory),
        handleAddGoal: trackEdit(handleAddGoal),
        handleDeleteBankAccount: trackEdit(handleDeleteBankAccount),
        handleDeleteCreditCard: trackEdit(handleDeleteCreditCard),
        handleDeleteInvestment: trackEdit(handleDeleteInvestment),
        handleDeleteAsset: trackEdit(handleDeleteAsset),
        handleDeleteHSA: trackEdit(handleDeleteHSA),
        handleDeleteCreditScore: trackEdit(handleDeleteCreditScore),
        handleDeleteLiability: trackEdit(handleDeleteLiability),
        handleDelete401kHolding: trackEdit(handleDelete401kHolding),
        handleDeleteCrypto: trackEdit(handleDeleteCrypto),
        handleDeletePrivateEquity: trackEdit(handleDeletePrivateEquity),
        handleClearAll401kHoldings: trackEdit(handleClearAll401kHoldings),
        handleDeleteBudgetCategory: trackEdit(handleDeleteBudgetCategory),
        handleDeleteGoal: trackEdit(handleDeleteGoal),
        handleBatchDelete: trackEdit(handleBatchDelete),
        getVisibleBankingData,
        getVisibleInvestments,
        getVisible401kHoldings,
        getVisibleCrypto,
        getVisiblePrivateEquity,
      }}
    >
      {children}
    </FinancialDataContext.Provider>
  );
};

export const useFinancialData = (): FinancialDataContextType => {
  const context = useContext(FinancialDataContext);
  if (context === undefined) {
    throw new Error('useFinancialData must be used within a FinancialDataProvider');
  }
  return context;
};
